import { supabase } from "../lib/supabase";

export const callsService = {
  // Get all calls for authenticated user based on role
  async getUserCalls(userId, userRole) {
    let query = supabase?.from('calls')?.select(`
        *,
        publisher:user_profiles!publisher_id(full_name, company_name),
        bids(id, bid_amount, buyer_id, status, created_at),
        auctions(id, total_bids, highest_bid, is_active)
      `)?.order('created_at', { ascending: false });

    if (userRole === 'publisher') {
      query = query?.eq('publisher_id', userId);
    } else if (userRole === 'buyer') {
      query = query?.in('status', ['pending', 'in_auction']);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Submit new call (publishers only)
  async submitCall(callData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Authentication required');

    const { data, error } = await supabase?.from('calls')?.insert({
        ...callData,
        publisher_id: user?.id,
        status: 'pending',
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString() // 24 hours
      })?.select(`
        *,
        publisher:user_profiles!publisher_id(full_name, company_name)
      `)?.single();

    if (error) throw error;
    return data;
  },

  // Bulk submit calls
  async submitBulkCalls(callsData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Authentication required');

    const callsWithPublisher = callsData?.map(call => ({
      ...call,
      publisher_id: user?.id,
      status: 'pending',
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString()
    }));

    const { data, error } = await supabase?.from('calls')?.insert(callsWithPublisher)?.select();

    if (error) throw error;
    return data || [];
  },

  // Get call details with bids
  async getCallDetails(callId) {
    const { data, error } = await supabase?.from('calls')?.select(`
        *,
        publisher:user_profiles!publisher_id(full_name, company_name),
        bids(
          id, bid_amount, status, created_at, auto_generated,
          buyer:user_profiles!buyer_id(full_name, company_name)
        ),
        auctions(id, total_bids, highest_bid, is_active, start_time, end_time)
      `)?.eq('id', callId)?.single();

    if (error) throw error;
    return data;
  },

  // Update call status
  async updateCallStatus(callId, status) {
    const { data, error } = await supabase?.from('calls')?.update({ 
        status,
        updated_at: new Date()?.toISOString(),
        ...(status === 'sold' ? { sold_at: new Date()?.toISOString() } : {})
      })?.eq('id', callId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Get calls by insurance type
  async getCallsByType(insuranceType) {
    const { data, error } = await supabase?.from('calls')?.select(`
        *,
        publisher:user_profiles!publisher_id(full_name),
        auctions(total_bids, highest_bid)
      `)?.eq('insurance_type', insuranceType)?.in('status', ['pending', 'in_auction'])?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Search calls with filters
  async searchCalls(filters = {}) {
    let query = supabase?.from('calls')?.select(`
        *,
        publisher:user_profiles!publisher_id(full_name),
        auctions(total_bids, highest_bid, is_active)
      `);

    if (filters?.insurance_type) {
      query = query?.eq('insurance_type', filters?.insurance_type);
    }

    if (filters?.call_quality) {
      query = query?.eq('call_quality', filters?.call_quality);
    }

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    } else {
      query = query?.in('status', ['pending', 'in_auction']);
    }

    if (filters?.min_premium) {
      query = query?.gte('estimated_premium', filters?.min_premium);
    }

    if (filters?.max_premium) {
      query = query?.lte('estimated_premium', filters?.max_premium);
    }

    if (filters?.zip_code) {
      query = query?.eq('customer_zip_code', filters?.zip_code);
    }

    const { data, error } = await query?.order('created_at', { ascending: false })?.limit(100);

    if (error) throw error;
    return data || [];
  },

  // Get performance stats for dashboard
  async getPerformanceStats(userId, dateRange = 30) {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - dateRange);

    const { data, error } = await supabase?.from('calls')?.select('id, status, final_price, created_at')?.eq('publisher_id', userId)?.gte('created_at', startDate?.toISOString());

    if (error) throw error;

    const stats = {
      total_calls: data?.length || 0,
      successful_calls: data?.filter(call => call?.status === 'sold')?.length || 0,
      pending_calls: data?.filter(call => call?.status === 'pending')?.length || 0,
      in_auction_calls: data?.filter(call => call?.status === 'in_auction')?.length || 0,
      total_revenue: data?.reduce((sum, call) => sum + (call?.final_price || 0), 0) || 0
    };

    stats.success_rate = stats?.total_calls > 0 ? (stats?.successful_calls / stats?.total_calls * 100) : 0;
    stats.avg_price = stats?.successful_calls > 0 ? (stats?.total_revenue / stats?.successful_calls) : 0;

    return stats;
  }
};